import { useState, useContext, useLayoutEffect } from 'react';
import { MyContext } from '../Store';
import { trans, getMatchSketches, getSelectedMod } from '../utils';
import { omit } from 'lodash';

export const Menu = () => {
  const [activeTab, setActiveTab] = useState('lines');
  const [state, dispatch] = useContext(MyContext);
  const { productData, productMods, selectedColor, selectedMods, selectedMarking } = state;

  const host = process.env.NODE_ENV === 'development' ? 'http://luva-test.pl:8000' : 'https://luva.menu';

  const excludingMods = {
    mountsList: ['202503649', '202503641', '202503640'],
    mountingSystemsList: ['152312301', '169528241'],
  };

  const isActive = value => activeTab === value;

  const isSizeActive = sizeID => (productData?.available_colors?.some(color => color.id === sizeID) ? 'active' : '');

  const isColorActive = color => (Number(selectedColor) === color.id ? 'menu__list--item-active' : '');

  const isModActive = (id, key) => selectedMods[key]?.id === id;

  const isSelectedExcludingMount = () =>
    Object.values(selectedMods).some(mod => excludingMods.mountsList.includes(mod.vendoCode));

  const isSelectedExcludingMountingSystem = () =>
    Object.values(selectedMods).some(mod => excludingMods.mountingSystemsList.includes(mod.vendoCode));

  const isModExcluded = vendoCode =>
    (excludingMods.mountingSystemsList.includes(vendoCode) && isSelectedExcludingMount()) ||
    (excludingMods.mountsList.includes(vendoCode) && isSelectedExcludingMountingSystem());

  const findConflictingMod = () =>
    Object.values(selectedMods).find(mod =>
      Object.values(excludingMods)
        .flatMap(code => code)
        .includes(mod.vendoCode),
    );

  const handleSelectColor = colorID => dispatch({ type: 'SET_SELECTED_COLOR', payload: colorID });

  const handleSelectMod = (modID, modGroup) => {
    const key = modGroup.id;
    const vendoCode = productData.vendo_code.slice(0, -2);

    if (selectedMods[key]?.id === modID) {
      // Cannot remove required modifications
      if (modGroup.isRequired) {
        return;
      }
      const payload = omit(selectedMods, key);
      dispatch({ type: 'SET_SELECTED_MODS', payload });
    } else {
      const selectedMod = getSelectedMod(productMods, modID);
      const payload = {
        ...selectedMods,
        [key]: {
          id: modID,
          name: selectedMod.name,
          vendoCode: selectedMod.vendoCode,
          sketches: getMatchSketches(selectedMod, vendoCode),
          topLayer: modGroup.topLayer,
        },
      };
      dispatch({ type: 'SET_SELECTED_MODS', payload });
    }
  };

  const handleSelectMarking = id => {
    const payload = selectedMarking.id === id ? {} : { ...selectedMarking, id };
    dispatch({ type: 'SET_SELECTED_MARKING', payload });
  };

  const filteredSizes = productData?.lines?.filter(line => line.line_size_name !== null);

  useLayoutEffect(() => {
    if (filteredSizes.length === 0) {
      setActiveTab('colors');
    }
  }, [filteredSizes.length]);

  return (
    <nav className="menu">
      {/* Size Selection */}
      {filteredSizes.length > 0 && (
        <div className="menu__size-select">
          <div
            className={`menu__link ${isActive('lines') ? 'menu__link--active' : ''} `}
            onClick={() => setActiveTab('lines')}
          >
            {trans('Rozmiar')}
          </div>
          {isActive('lines') && (
            <div className="menu__list">
              {filteredSizes.map((line, index) => (
                <a
                  href={`${host}${line.url.slice(0, -2)}?configuratorOpen=true`}
                  key={`size-${index}`}
                  className={`size ${line.line_size_name?.replaceAll('.', '-').toLowerCase()} ${isSizeActive(line.id)}`}
                  target="_parent"
                >
                  {line.line_size_name}
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Color Selection */}
      <div className="menu__color-select">
        <div
          className={`menu__link ${isActive('colors') ? 'menu__link--active' : ''} `}
          onClick={() => setActiveTab('colors')}
        >
          {trans('Kolor')}
        </div>
        {isActive('colors') && (
          <div className="menu__list">
            {productData?.available_colors?.map(color => (
              <div
                className={`menu__list--item is-required ${isColorActive(color)}`}
                onClick={() => handleSelectColor(color.id)}
                key={`colorItem${color.id}`}
              >
                <figure className="menu__list--item-image">
                  <img src={color.color_image || `${process.env.PUBLIC_URL}/no-image.png`} alt={color.color.name} />
                </figure>
                <div className="menu__list--item-details">{color.color.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mod Selection */}
      {productMods?.map((modGroup, index) => (
        <div key={`modGroup${modGroup.id}`}>
          <div
            className={`menu__link ${isActive(`modGroup${index}`) ? 'menu__link--active' : ''} `}
            onClick={() => setActiveTab(`modGroup${index}`)}
          >
            {modGroup.name || 'Pozostałe'}
          </div>
          {isActive(`modGroup${index}`) && (
            <div className="menu__list">
              {modGroup.items?.map(mod => (
                <div
                  className={`menu__list--item ${isModActive(mod.id, modGroup.id) ? 'menu__list--item-active' : ''} ${
                    modGroup.isRequired ? 'is-required' : ''
                  } ${isModExcluded(mod.vendoCode) ? 'menu__list--item-excluded' : ''}`}
                  onClick={() => !isModExcluded(mod.vendoCode) && handleSelectMod(mod.id, modGroup)}
                  key={`modItem${mod.id}`}
                >
                  <figure className="menu__list--item-image">
                    <img src={mod.image?.image || `${process.env.PUBLIC_URL}/no-image.png`} alt={mod.name} />
                  </figure>
                  <div className="menu__list--item-details">{mod.name}</div>
                  {isModExcluded(mod.vendoCode) && (
                    <div className="menu__list--item-conflicting">
                      Niedostępne ze względu na wybór: <br /> {findConflictingMod()?.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Marking Selection */}
      <div>
        <div
          className={`menu__link ${isActive('marking') ? 'menu__link--active' : ''} `}
          onClick={() => setActiveTab('marking')}
        >
          {trans('Znakowanie')}
        </div>
        {isActive('marking') && (
          <div className="menu__list">
            {productData?.marking?.map(marking => (
              <div
                className={`menu__list--item ${selectedMarking.id === marking.id ? 'menu__list--item-active' : ''}`}
                onClick={() => handleSelectMarking(marking.id)}
                key={`markingItem${marking.id}`}
              >
                <figure className="menu__list--item-image">
                  <img src={marking.icon_image || `${process.env.PUBLIC_URL}/no-image.png`} alt={marking.name} />
                </figure>
                <div className="menu__list--item-details">{marking.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};
